import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import logger from "../lib/logger";
import { setProgress, clearProgress } from "../lib/practiceProgress";
import { postStreakEvent } from "../lib/streak";

// ============================================================================
// Library audio context
// ----------------------------------------------------------------------------
// A single global <audio> element + Web Audio panner graph that survives route
// changes and keeps playing when the tab is backgrounded. Pages render a UI
// against this context; they no longer own the audio element.
//
// Why this is necessary:
//   1. Route changes unmount per-page audio elements and stop playback.
//   2. Mobile browsers suspend tabs that don't have an actively-rendered
//      <audio> or MediaSession entry — keeping a single element mounted at
//      App root + advertising MediaSession metadata signals the OS that this
//      tab is "playing media" so background playback is preserved.
// ============================================================================

const LibraryAudioContext = createContext(null);

export const useLibraryAudio = () => {
  const ctx = useContext(LibraryAudioContext);
  if (!ctx) {
    throw new Error("useLibraryAudio must be used within LibraryAudioProvider");
  }
  return ctx;
};

export const LibraryAudioProvider = ({ children }) => {
  // ---- Singleton DOM + Web Audio refs ----
  const audioRef = useRef(null);          // the <audio> element
  const audioCtxRef = useRef(null);       // AudioContext
  const sourceRef = useRef(null);         // MediaElementAudioSourceNode
  const pannerRef = useRef(null);         // StereoPannerNode
  const rafRef = useRef(null);            // requestAnimationFrame for pan sweep

  // ---- Reactive state surfaced to consumers ----
  const [practice, setPractice] = useState(null); // currently loaded practice
  // Mirror of `practice` for use inside async callbacks (e.g. the
  // meditation TTS fetch resolver) where reading the closed-over
  // `practice` would yield a stale value.
  const practiceRef = useRef(null);
  useEffect(() => { practiceRef.current = practice; }, [practice]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.7);
  const [bilateralOn, setBilateralOn] = useState(true);
  const [error, setError] = useState(null);

  // ---- Lazily build the Web Audio graph the first time playback starts ----
  // (autoplay policies require user gesture; that gesture is the play tap.)
  //
  // Graph shape:
  //     source ──► panner ──► destination
  // For voice-only practices we DISCONNECT the panner and wire the source
  // directly to the destination so nothing sits in the middle that could
  // attenuate or silence the signal on misbehaving decoders.
  //
  // ⚠️ iOS Capacitor exception: WKWebView's implementation of
  // `createMediaElementSource` doesn't fully mute the underlying
  // <audio> element's direct output, so users heard the meditation
  // audio through *two paths at once* (Web Audio graph + raw element)
  // — producing an obvious echo. On Capacitor iOS we skip the Web
  // Audio graph entirely and let the <audio> element play through the
  // standard iOS media path. Bilateral panning is disabled for
  // meditation on iOS as a result; the pre-recorded MP3s are already
  // stereo-mixed so the practice still delivers.
  const isCapacitorIOS = (() => {
    try {
      // eslint-disable-next-line global-require
      const { Capacitor } = require("@capacitor/core");
      return Capacitor?.getPlatform?.() === "ios";
    } catch { return false; }
  })();

  const ensureAudioGraph = useCallback(() => {
    if (isCapacitorIOS) return;                    // see comment above
    if (audioCtxRef.current || !audioRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return; // graceful fallback to plain stereo
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audioRef.current);
      const panner = ctx.createStereoPanner();
      // Initial routing: source straight to destination (default, always
      // produces audible output regardless of pan/practice config).
      source.connect(ctx.destination);
      // Panner is built but not wired into the graph yet — `routeAudio`
      // moves the source to the panner branch when bilateral is enabled.
      panner.connect(ctx.destination);
      // Immediately re-route based on which kind of practice is loaded.
      audioCtxRef.current = ctx;
      sourceRef.current = source;
      pannerRef.current = panner;
    } catch (e) {
      // MediaElementSource can only be created once per element; if it throws
      // we silently fall back to plain stereo playback.
    }
  }, [isCapacitorIOS]);

  // Switches the audio graph between "direct" (source→destination) and
  // "panned" (source→panner→destination). Called whenever the loaded
  // practice or the bilateral toggle changes.
  const routeAudio = useCallback((useBilateral) => {
    const source = sourceRef.current;
    const panner = pannerRef.current;
    const ctx = audioCtxRef.current;
    if (!source || !panner || !ctx) return;
    try {
      source.disconnect();
      if (useBilateral) {
        source.connect(panner);
      } else {
        source.connect(ctx.destination);
        // Reset pan position so the next bilateral practice starts centered.
        panner.pan.value = 0;
      }
    } catch (e) {
      // disconnect() can throw if the node was already disconnected; ignore.
    }
  }, []);

  // Whenever the bilateral toggle changes (or a new practice loads with a
  // different bilateralCapable flag), re-wire the audio graph to either
  // route through the panner or straight to the destination.
  useEffect(() => {
    const useBilateral = Boolean(practice?.bilateralCapable && bilateralOn);
    routeAudio(useBilateral);
  }, [practice, bilateralOn, routeAudio]);

  // When the page becomes visible again (user unlocked phone / refocused
  // the tab), resume the AudioContext if iOS suspended it while the
  // screen was off. Without this, Web Audio playback stays silent until
  // the user manually taps play again.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        audioCtxRef.current?.state === "suspended"
      ) {
        audioCtxRef.current.resume().catch(() => { /* non-fatal */ });
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // ---- Bilateral pan animation (sine wave at practice.bilateralBpm) ----
  useEffect(() => {
    const stop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (pannerRef.current) pannerRef.current.pan.value = 0;
    };

    if (
      !isPlaying ||
      !bilateralOn ||
      !pannerRef.current ||
      !audioCtxRef.current ||
      !practice?.bilateralCapable
    ) {
      stop();
      return;
    }

    const bpm = practice.bilateralBpm || 50;
    const periodSec = 120 / bpm; // one full L↔R↔L cycle
    const ctx = audioCtxRef.current;
    const tick = () => {
      if (!pannerRef.current) return;
      const t = ctx.currentTime;
      pannerRef.current.pan.value = Math.sin((2 * Math.PI * t) / periodSec);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return stop;
  }, [isPlaying, bilateralOn, practice]);

  // ---- Wire core <audio> element listeners + volume ----
  // Throttle ref for the "Resume where you left off" persistence — we
  // only stamp localStorage at most once every PROGRESS_SAVE_MS so the
  // ~4 timeupdate callbacks per second don't hammer the disk.
  const lastSavedRef = useRef(0);
  const PROGRESS_SAVE_MS = 5000;
  // Per-practice streak-event de-duplication. When the listener crosses
  // 120 s on a given practice within a single page-load session, we
  // fire the streak event ONCE. A new practice or a fresh page-load
  // resets this so a subsequent listen (on a different day) can count.
  const streakFiredRef = useRef(new Set());
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      // Persist the resume point for the currently-loaded practice.
      const p = practiceRef.current;
      if (!p || !audio.duration) return;
      // Streak event — 2+ minutes of continuous playback on any single
      // library practice counts as a streak day. Fire once per
      // (practice, session) so scrubbing forward doesn't game it.
      if (audio.currentTime >= 120 && !streakFiredRef.current.has(p.id)) {
        streakFiredRef.current.add(p.id);
        postStreakEvent({ source: "library", refId: p.id }).catch(() => {});
      }
      const now = Date.now();
      if (now - lastSavedRef.current < PROGRESS_SAVE_MS) return;
      lastSavedRef.current = now;
      setProgress(p.id, audio.currentTime, audio.duration);
    };
    const onLoaded = () => {
      setDuration(audio.duration);
      // Successful load — clear any stale error from a previous attempt
      // or from the intentional empty-src reset between track switches.
      setError(null);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      // Track finished naturally — wipe any saved resume position so
      // the card resets to a fresh play next time.
      const p = practiceRef.current;
      if (p) clearProgress(p.id);
    };
    const onError = () => {
      // Browsers fire `error` when the <audio> src is intentionally
      // cleared (set to "") between meditation track switches while
      // we wait for the stitched-TTS URL to come back from the API.
      // Only surface a real load failure — ignore the empty-src case.
      if (!audio.src || audio.src.endsWith("/")) return;
      setError(
        "We couldn't load this audio yet. Please try again in a moment."
      );
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // ---- MediaSession API (lock-screen / OS controls + background hint) ----
  useEffect(() => {
    if (!("mediaSession" in navigator) || !practice) return;
    try {
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: practice.title,
        artist: "Bilateral Calm",
        album: practice.category || "Daily Practice",
        artwork: practice.image
          ? [{ src: practice.image, sizes: "512x512", type: "image/jpeg" }]
          : [],
      });
      navigator.mediaSession.setActionHandler("play", () => audioRef.current?.play());
      navigator.mediaSession.setActionHandler("pause", () => audioRef.current?.pause());
      navigator.mediaSession.setActionHandler("seekto", (d) => {
        if (audioRef.current && Number.isFinite(d.seekTime)) {
          audioRef.current.currentTime = d.seekTime;
        }
      });
    } catch (e) {
      // older browsers — silently ignore
    }
  }, [practice]);

  // ---- Public API ----
  const load = useCallback(
    (next) => {
      if (!next) return;
      const switching = !practice || practice.id !== next.id;
      setError(null);
      setPractice(next);
      // Default the bilateral toggle to ON if the new practice supports it,
      // OFF otherwise — but only when we're actually switching tracks so we
      // don't override the user's manual toggle for the same track.
      if (switching) {
        setBilateralOn(Boolean(next.bilateralCapable));
        if (audioRef.current) {
          if (next.script) {
            // Guided meditation rendered through the TTS narrator —
            // fetch the stitched MP3 URL from the backend (cached
            // server-side by script hash). While we wait, keep the
            // <audio> element src empty so we don't play the static
            // audioFile by accident.
            audioRef.current.src = "";
            const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
            axios.post(`${API}/narration/meditation`, {
              practice_id: next.id,
              title: next.title,
              script: next.script,
            }, { withCredentials: true })
              .then((res) => {
                // Race-guard: only apply if the user is still on this practice.
                if (!audioRef.current) return;
                const latestPractice = practiceRef.current;
                if (latestPractice && latestPractice.id !== next.id) return;
                audioRef.current.src = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                audioRef.current.load();
              })
              .catch((err) => {
                logger.error("meditation narration fetch failed", err?.message || err);
                setError("Couldn't load this meditation. Please try again in a moment.");
              });
          } else {
            audioRef.current.src = next.audioFile;
            audioRef.current.load();
          }
        }
      }
    },
    [practice]
  );

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      // Only build the Web Audio graph (StereoPanner + MediaElementSource)
      // when bilateral panning is actually needed. Voice-only meditations
      // skip Web Audio entirely so iOS Safari plays them through native
      // HTML5 media, which keeps playing with the phone screen locked.
      // Once a MediaElementSource is attached to an audio element it can
      // never be detached — so for the FIRST playback we have to make the
      // right choice.
      const needsBilateral = Boolean(practice?.bilateralCapable);
      if (needsBilateral) {
        ensureAudioGraph();
        const useBilateral = Boolean(bilateralOn);
        routeAudio(useBilateral);
        if (audioCtxRef.current?.state === "suspended") {
          await audioCtxRef.current.resume();
        }
      } else if (audioCtxRef.current) {
        // We're playing a voice meditation but the graph already exists
        // (because a bilateral track was played earlier in this session).
        // Route the source straight to the destination + resume the ctx
        // so the meditation still produces sound.
        routeAudio(false);
        if (audioCtxRef.current.state === "suspended") {
          await audioCtxRef.current.resume();
        }
      }
      await audio.play();
    } catch (e) {
      setError("Playback was blocked. Tap play again to begin.");
    }
  }, [ensureAudioGraph, routeAudio, practice, bilateralOn]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) await play();
    else pause();
  }, [play, pause]);

  const seekTo = useCallback((seconds) => {
    if (audioRef.current) audioRef.current.currentTime = seconds;
  }, []);

  const restart = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      const p = practiceRef.current;
      if (p) clearProgress(p.id);
      if (!audioRef.current.paused) audioRef.current.play();
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPractice(null);
  }, []);

  const setVolume = useCallback((v) => {
    setVolumeState(Math.max(0, Math.min(1, v)));
  }, []);

  const value = {
    practice,
    isPlaying,
    currentTime,
    duration,
    volume,
    bilateralOn,
    error,
    setBilateralOn,
    load,
    play,
    pause,
    toggle,
    seekTo,
    restart,
    stop,
    setVolume,
  };

  return (
    <LibraryAudioContext.Provider value={value}>
      {/* Singleton audio element — must stay mounted for the entire app
          lifetime so the OS treats this tab as "playing media" and so the
          Web Audio graph created against it isn't torn down on route change. */}
      <audio
        ref={audioRef}
        preload="metadata"
        playsInline
        data-testid="library-audio-singleton"
      />
      {children}
    </LibraryAudioContext.Provider>
  );
};

export default LibraryAudioProvider;
